// src/host/HostPayouts.js
// Uses your Cloud Run/Firebase Function base and posts to `${BASE}/rpc` with Firebase ID token.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../lib/useAuth";

// ---------- CONFIG: function base (Vite or Next supported) ----------
const STRIPE_BASE =
  (typeof import.meta !== "undefined" &&
    (import.meta.env?.VITE_STRIPE_FN_PATH || import.meta.env?.NEXT_PUBLIC_STRIPE_FN_PATH)) ||
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_STRIPE_FN_PATH) ||
  "https://stripe-llwk7pksyq-uc.a.run.app"; // fallback: your Cloud Run URL
const STRIPE_RPC = `${String(STRIPE_BASE).replace(/\/+$/, "")}/rpc`;

function formatMoney(cents, currency = "usd") {
  if (cents == null || isNaN(cents)) return "—";
  const amount = Number(cents) / 100;
  try {
    return amount.toLocaleString(undefined, {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
      maximumFractionDigits: 2,
    });
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function Badge({ tone = "neutral", children }) {
  const tones = {
    neutral: "border-neutral-300 bg-white text-neutral-700",
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    bad: "border-red-200 bg-red-50 text-red-700",
    info: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

async function postStripe(action, payload = {}) {
  const auth = getAuth();
  const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;

  const res = await fetch(STRIPE_RPC, {
    method: "POST",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ action, ...payload }),
  });

  let json = null;
  try { json = await res.json(); } catch (_) {}

  if (!res.ok) {
    const msg =
      json?.error ||
      json?.message ||
      (res.status === 404
        ? `Stripe RPC 404 at ${STRIPE_RPC}. Check STRIPE_BASE is correct and that /rpc is the path.`
        : `Stripe call failed (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json || {};
}

export default function HostPayouts() {
  const { user, loading } = useAuth();
  const db = getFirestore();

  const [uDoc, setUDoc] = useState(null);
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(""); // '', 'onboarding', 'dashboard', 'refresh', 'payouts'
  const [payouts, setPayouts] = useState([]);
  const [payoutsError, setPayoutsError] = useState("");
  const [payoutsSupported, setPayoutsSupported] = useState(true);

  // Live Firestore user doc
  useEffect(() => {
    if (loading || !user) return;
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => setUDoc(snap.exists() ? snap.data() : {}),
      (err) => {
        console.error(err);
        setFlash("Couldn't load your payout settings.");
      }
    );
    return () => unsub();
  }, [db, loading, user]);

  // Shape we expect in Firestore:
  // user.stripeAccountId (string) or user.stripe.accountId
  const stripe = uDoc?.stripe || {};
  const accountId = stripe.accountId ?? uDoc?.stripeAccountId ?? null;
  const { chargesEnabled, payoutsEnabled, detailsSubmitted, requirementsDue, defaultBankBrand, defaultBankLast4 } = stripe;

  const needsOnboarding = useMemo(() => {
    const hasDue = Array.isArray(requirementsDue) && requirementsDue.length > 0;
    const explicitlyIncomplete = detailsSubmitted === false;
    return Boolean(accountId && (explicitlyIncomplete || hasDue));
  }, [accountId, detailsSubmitted, requirementsDue]);

  const connectionState = useMemo(() => {
    if (!accountId) return { label: "Not connected", tone: "bad" };
    if (Array.isArray(requirementsDue) && requirementsDue.length > 0) return { label: "Action required", tone: "warn" };
    if (detailsSubmitted === true && payoutsEnabled === true) return { label: "Connected", tone: "good" };
    if (detailsSubmitted === true && payoutsEnabled !== true) return { label: "Pending payout enablement", tone: "info" };
    if (typeof detailsSubmitted !== "boolean" && typeof payoutsEnabled !== "boolean") return { label: "Account created", tone: "info" };
    return { label: "Onboarding in progress", tone: "info" };
  }, [accountId, detailsSubmitted, payoutsEnabled, requirementsDue]);

  // Ensure an Express account exists (server will create if missing)
  const ensureAccount = useCallback(async () => {
    try {
      await postStripe("ensureExpressAccount", {});
      return true;
    } catch (e) {
      console.error(e);
      setFlash(e?.message || "Couldn't verify Stripe account.");
      return false;
    }
  }, []);

  // NEW: ask server to sync latest Stripe flags into Firestore (host.syncAccount)
  const refreshStripeFlags = useCallback(async () => {
    try { await postStripe("host.syncAccount"); } catch (_) {}
  }, []);

  const handleCreateOrContinueOnboarding = useCallback(async () => {
    if (!user) return;
    setBusy("onboarding");
    setFlash("");
    try {
      await ensureAccount(); // safe even if already exists
      const { url } = await postStripe("createExpressOnboardingLink", {});
      if (!url) throw new Error("Onboarding link missing from server.");
      window.location.assign(url);
    } catch (e) {
      console.error(e);
      setFlash(e?.message || "Couldn't open Stripe onboarding.");
    } finally {
      setBusy("");
    }
  }, [user, ensureAccount]);

  const handleOpenStripeDashboard = useCallback(async () => {
    setBusy("dashboard");
    setFlash("");
    try {
      const { url } = await postStripe("createExpressDashboardLink", {});
      if (!url) throw new Error("Dashboard link missing from server.");
      window.location.assign(url);
    } catch (e) {
      console.error(e);
      setFlash(e?.message || "Couldn't open Stripe dashboard.");
    } finally {
      setBusy("");
    }
  }, []);

  const handleRefreshStatus = useCallback(async () => {
    setBusy("refresh");
    setFlash("");
    try {
      await ensureAccount();
      await refreshStripeFlags(); // <-- persist fresh flags your UI reads
      setFlash("Stripe status refreshed.");
    } finally {
      setBusy("");
    }
  }, [ensureAccount, refreshStripeFlags]);

  const loadPayouts = useCallback(async () => {
    if (!accountId) {
      setPayouts([]);
      return;
    }
    setBusy("payouts");
    setPayoutsError("");
    setPayoutsSupported(true);
    try {
      // FIX: parse Stripe list object
      const { payouts: list } = await postStripe("host.listPayouts", { account: accountId });
      setPayouts(Array.isArray(list?.data) ? list.data : []);
    } catch (e) {
      console.warn("Payout listing not available:", e?.message);
      setPayoutsSupported(false);
      setPayoutsError("Payout history isn’t available in-app yet. Use Stripe Dashboard instead.");
    } finally {
      setBusy("");
    }
  }, [accountId]);

  // Ensure account exists once user loads the page
  useEffect(() => {
    if (!user) return;
    ensureAccount();
  }, [user, ensureAccount]);

  // Try to load payouts if an account exists
  useEffect(() => {
    if (accountId) loadPayouts();
  }, [accountId, loadPayouts]);

  // Refresh when user returns from Stripe (tab focus)
  useEffect(() => {
    const onFocus = () => handleRefreshStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [handleRefreshStatus]);

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Payouts</h1>
        <p className="mt-2 text-neutral-600">Connect payouts and view transfer history.</p>
        <div className="mt-6 rounded-2xl border border-neutral-200 p-6">
          <div className="h-24 animate-pulse rounded-xl bg-neutral-200" />
        </div>
      </div>
    );
  }
  if (!user) return null;

  const isBusy = (k) => busy === k;

  return (
    <div>
      <h1 className="text-2xl font-semibold">Payouts</h1>
      <p className="mt-2 text-neutral-600">
        Connect your Stripe account to receive payouts. We never store your bank information—Stripe handles it securely.
      </p>

      {!!flash && (
        <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-800">
          {flash}
        </div>
      )}

      {/* Connection card */}
      <section className="mt-6 grid gap-6 lg:grid-cols-[1fr,1fr]">
        <div className="rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Stripe connection</h2>
            <Badge tone={connectionState.tone}>{connectionState.label}</Badge>
          </div>

          <div className="mt-3 text-sm text-neutral-600">
            {accountId ? (
              <>
                <div>
                  Account: <span className="font-mono text-neutral-800">{accountId}</span>
                </div>
                <div className="mt-1">
                  Payouts: <strong>{typeof payoutsEnabled === "boolean" ? (payoutsEnabled ? "Enabled" : "Disabled") : "Unknown"}</strong>{" "}
                  · Charges: <strong>{typeof chargesEnabled === "boolean" ? (chargesEnabled ? "Enabled" : "Disabled") : "Unknown"}</strong>
                </div>
                {defaultBankBrand || defaultBankLast4 ? (
                  <div className="mt-1">
                    Bank: {defaultBankBrand || "••••"} •••• {defaultBankLast4 || "••••"}
                  </div>
                ) : (
                  <div className="mt-1 text-neutral-500">
                    Bank account will appear here after onboarding (or via a future status sync).
                  </div>
                )}
                {Array.isArray(requirementsDue) && requirementsDue.length > 0 && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Stripe needs additional info:
                    <ul className="mt-1 list-disc pl-6">
                      {requirementsDue.map((k) => (
                        <li key={k} className="font-mono">
                          {k}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div>No connected account yet.</div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {!accountId ? (
              <button
                onClick={handleCreateOrContinueOnboarding}
                disabled={isBusy("onboarding")}
                className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-[#B8860B] disabled:opacity-60"
              >
                {isBusy("onboarding") ? "Opening…" : "Connect with Stripe"}
              </button>
            ) : needsOnboarding ? (
              <button
                onClick={handleCreateOrContinueOnboarding}
                disabled={isBusy("onboarding")}
                className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-60"
              >
                {isBusy("onboarding") ? "Opening…" : "Resume onboarding"}
              </button>
            ) : (
              <button
                onClick={handleOpenStripeDashboard}
                disabled={isBusy("dashboard")}
                className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-60"
              >
                {isBusy("dashboard") ? "Opening…" : "Open Stripe Dashboard"}
              </button>
            )}

            <button
              onClick={handleRefreshStatus}
              disabled={isBusy("refresh")}
              className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-60"
              title="Re-check connection"
            >
              {isBusy("refresh") ? "Refreshing…" : "Refresh status"}
            </button>
          </div>

          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-700">
            <strong>Heads up:</strong> Stripe Express onboarding will collect identity verification and payout bank details
            directly on Stripe. We do <em>not</em> collect or store your bank account numbers.
          </div>
        </div>

        {/* Payouts / transfers */}
        <div className="rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Payout history</h2>
            <button
              onClick={loadPayouts}
              disabled={!accountId || isBusy("payouts")}
              className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-50 disabled:opacity-60"
            >
              {isBusy("payouts") ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {!accountId ? (
            <p className="mt-3 text-sm text-neutral-600">Connect with Stripe to see payouts here.</p>
          ) : payoutsError ? (
            <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              {payoutsError}{" "}
              <button
                onClick={handleOpenStripeDashboard}
                className="ml-2 inline-flex items-center rounded-full border border-blue-300 px-3 py-1 text-xs font-semibold hover:bg-blue-100"
              >
                View in Stripe
              </button>
            </div>
          ) : payouts.length === 0 ? (
            <p className="mt-3 text-sm text-neutral-600">
              {payoutsSupported
                ? "No payouts yet. Payouts usually arrive 2–7 business days after a completed booking (varies by country)."
                : "In-app payout history isn’t available yet. Open the Stripe Dashboard to view transfers and payouts."}
            </p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Method</th>
                    <th className="px-3 py-2">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-t border-neutral-200">
                      <td className="px-3 py-2">{p.arrival_date ? new Date(p.arrival_date * 1000).toLocaleDateString() : "—"}</td>
                      <td className="px-3 py-2 font-medium">{formatMoney(p.amount, p.currency)}</td>
                      <td className="px-3 py-2">
                        <Badge tone={p.status === "paid" ? "good" : p.status === "pending" ? "info" : "warn"}>
                          {String(p.status || "—").toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{p.method ? String(p.method).toUpperCase() : "—"}</td>
                      <td className="px-3 py-2">{p.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-700">
            <strong>Tip:</strong> For payout schedule and multiple bank accounts, use the Stripe Dashboard.
          </div>
        </div>
      </section>

      {/* Developer notes */}
      <details className="mt-6 rounded-2xl border border-neutral-200 p-4 text-sm text-neutral-600">
        <summary className="cursor-pointer font-semibold text-neutral-800">Developer notes</summary>
        <ul className="mt-2 list-disc pl-5 leading-6">
          <li>
            Calling <code>{STRIPE_RPC}</code> (base <code>{STRIPE_BASE}</code>) with <code>Authorization: Bearer &lt;Firebase ID token&gt;</code>.
          </li>
          <li>Server actions used: <code>ensureExpressAccount</code>, <code>createExpressOnboardingLink</code>, <code>createExpressDashboardLink</code>, <code>host.listPayouts</code>, <code>host.syncAccount</code>.</li>
        </ul>
      </details>
    </div>
  );
}
